import { useQuery } from '@tanstack/react-query'
import { useProfileStore } from '@/store/profileStore'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/authStore'

interface ProfilePhotoData {
  url: string
  timestamp: number
}

// Utility function to extract storage path from a full Supabase URL
function extractStoragePathFromUrl(url: string): string | null {
  try {
    // Pattern: https://[project].supabase.co/storage/v1/object/public/profiles/[path]
    const match = url.match(/\/storage\/v1\/object\/public\/profiles\/(.+)/)
    return match ? match[1] : null
  } catch {
    return null
  }
}

// Utility function to migrate a full URL to storage path format
async function migratePhotoUrlToStoragePath(userId: string, photoUrl: string): Promise<void> {
  if (!photoUrl.startsWith('http')) return // Already a storage path
  
  const storagePath = extractStoragePathFromUrl(photoUrl)
  if (!storagePath) return // Can't extract path
  
  try {
    console.log(`Migrating profile photo URL to storage path for user ${userId}`)
    
    // Update the database with the storage path instead of full URL
    const { error } = await supabase
      .from('profiles')
      .update({ 
        photo_url: storagePath,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
    
    if (error) {
      console.error('Failed to migrate photo URL:', error)
    }
  } catch (error) {
    console.error('Error during photo URL migration:', error)
  }
}

export function useProfilePhoto() {
  const photoUrl = useProfileStore((state: any) => state.settings?.photo_url)
  const { user } = useAuthStore()

  const query = useQuery({
    queryKey: ['profilePhoto', photoUrl],
    queryFn: async (): Promise<ProfilePhotoData> => {
      if (!photoUrl) {
        return { url: '', timestamp: Date.now() }
      }

      // Check if photoUrl is already a full URL or just a storage path
      if (photoUrl.startsWith('http')) {
        // It's a full URL - migrate it to storage path format if possible
        if (user?.id) {
          await migratePhotoUrlToStoragePath(user.id, photoUrl)
        }
        
        // Return the URL directly while migration happens in background
        return {
          url: photoUrl,
          timestamp: Date.now()
        }
      } else {
        // It's a storage path, generate a signed URL
        try {
          const { data, error } = await supabase.storage
            .from('profiles')
            .createSignedUrl(photoUrl, 60 * 60 * 24) // 24 hours expiry
          
          if (error) {
            console.error('Error creating signed URL for profile photo:', error)
            // Fallback to public URL if signed URL fails
            const { data: publicUrlData } = supabase.storage
              .from('profiles')
              .getPublicUrl(photoUrl)
            
            return {
              url: publicUrlData?.publicUrl || '',
              timestamp: Date.now()
            }
          }
          
          return {
            url: data?.signedUrl || '',
            timestamp: Date.now()
          }
        } catch (error) {
          console.error('Error processing profile photo storage path:', error)
          return { url: '', timestamp: Date.now() }
        }
      }
    },
    gcTime: 30 * 60 * 1000, // Cache for 30 minutes
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    enabled: !!photoUrl,
    retry: 2, // Retry up to 2 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  return query
} 